import React, { useState, useEffect, memo } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Loader,
  Check,
  FileText,
  FolderPlus,
  LayoutGrid,
  ListPlus,
  Plus,
  Trash2,
  Pencil,
  ExternalLink,
} from 'lucide-react';
import AiApprovalCard from './AiApprovalCard';
import { describeToolCall } from './toolPresentation';
import { renderToolResult } from './toolResultViews';
import ToolCodeBlock from './ToolCodeBlock';

// ── Sub-components ─────────────────────────────────────────────────────────────

export function TypingIndicator() {
  return (
    <span className="ai-chat-typing" aria-label="Assistant is typing">
      <span className="ai-chat-typing-dot" />
      <span className="ai-chat-typing-dot" />
      <span className="ai-chat-typing-dot" />
    </span>
  );
}

export const ToolCallCard = memo(function ToolCallCard({ toolName, toolInput, toolResult, interactive, awaitingConfirm, onConfirm }) {
  const [open, setOpen] = useState(toolResult !== undefined && !interactive);
  const summary = describeToolCall(toolName, toolInput);

  useEffect(() => {
    if (toolResult !== undefined && !interactive && !awaitingConfirm) setOpen(true);
  }, [toolResult, interactive, awaitingConfirm]);

  // Confirm gate (Auto-run off, or always for destructive): approval card.
  // AiApprovalCard shows the command / SQL / URL in full — see its header.
  if (toolResult && toolResult.__awaitingConfirm) {
    return (
      <AiApprovalCard
        toolName={toolName}
        toolInput={toolInput}
        awaitingConfirm={awaitingConfirm}
        onConfirm={onConfirm}
      />
    );
  }

  // write_document always opens the doc now, so render a clean, tool-name-free
  // status regardless of the interactive toggle.
  if (toolName === 'write_document') {
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>{toolInput?.mode === 'append' ? 'Appending to document…' : 'Writing document…'}</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          Couldn’t write the document: {toolResult.error}
        </div>
      );
    }
    if (toolResult?.cancelled) {
      return <div className="mt-2 text-xs text-gray-400 dark:text-zinc-500">Cancelled</div>;
    }
    if (toolResult !== undefined) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <FileText size={13} className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
          <span>
            {toolInput?.mode === 'append' ? 'Updated document' : 'Wrote document'}
            <span className="text-gray-400 dark:text-zinc-500"> · Documents</span>
          </span>
        </div>
      );
    }
  }

  if (interactive && toolName === 'run_collection') {
    const label = toolInput?.collection_name || toolInput?.collection_id || 'collection';
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>Running {label} in Run Collection…</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          <span className="font-mono font-semibold">run_collection</span>
          {' '}failed: {toolResult.error}
        </div>
      );
    }
    if (toolResult !== undefined) {
      const statusClass = toolResult.failed_count > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400';
      return (
        <div className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
          <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">run_collection</span>
          {' '}{toolResult.collection_name || label}
          {' → '}
          <span className={statusClass}>
            {toolResult.passed_count}/{toolResult.total} passed
          </span>
          {toolResult.total_time_ms != null && ` (${toolResult.total_time_ms}ms)`}
          <span className="text-gray-400 dark:text-zinc-500"> · Run Collection</span>
        </div>
      );
    }
  }

  // P1: test generation — one-line pass/fail summary (the script is written to the
  // request's Tests tab, marked unsaved for the user to review/save).
  if (toolName === 'generate_tests' && toolResult !== undefined && toolResult !== null) {
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          <span className="font-mono font-semibold">generate_tests</span> failed: {toolResult.error}
        </div>
      );
    }
    const ran = toolResult.ran_against_stored_response;
    const failed = toolResult.failed_count > 0;
    const statusClass = failed ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400';
    return (
      <div className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
        <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">generate_tests</span>
        {' → written to Tests tab'}
        {ran ? (
          <>
            {' · '}
            <span className={statusClass}>{toolResult.passed_count} passed, {toolResult.failed_count} failed</span>
          </>
        ) : (
          <span className="text-gray-400 dark:text-zinc-500"> · send the request to run them</span>
        )}
        <span className="text-gray-400 dark:text-zinc-500"> · unsaved — review &amp; save</span>
      </div>
    );
  }

  // P1: pre-request script — one-line dry-run summary. The script is written to
  // the request's Pre-request tab, marked unsaved. The dry run applies nothing,
  // which the card says out loud so the user does not think their environment
  // just changed.
  if (toolName === 'generate_pre_request_script' && toolResult !== undefined && toolResult !== null) {
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          <span className="font-mono font-semibold">generate_pre_request_script</span> failed: {toolResult.error}
        </div>
      );
    }
    const dry = toolResult.dry_run || {};
    const wouldSet = [
      ...(dry.would_set_environment_variables || []),
      ...(dry.would_set_local_variables || []),
    ];
    return (
      <div className="mt-2 text-xs text-gray-500 dark:text-zinc-400">
        <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">generate_pre_request_script</span>
        {' → written to Pre-request tab'}
        {' · '}
        {dry.ok ? (
          <span className="text-green-600 dark:text-green-400">
            dry run ok
            {wouldSet.length > 0 && ` — would set ${wouldSet.join(', ')}`}
          </span>
        ) : (
          <span className="text-amber-600 dark:text-amber-400">dry run error: {dry.error}</span>
        )}
        <span className="text-gray-400 dark:text-zinc-500"> · nothing applied — unsaved, review &amp; save</span>
      </div>
    );
  }

  // Documents: always render a clean one-line status (no tool name / input /
  // result collapsible). The document is opened in the Documents panel, so the
  // chat just needs a friendly confirmation.
  if (toolName === 'create_document') {
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>Creating document{toolInput?.title ? ` “${toolInput.title}”` : ''}…</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          Couldn’t create the document: {toolResult.error}
        </div>
      );
    }
    if (toolResult !== undefined) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <FileText size={13} className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
          <span>
            Created <span className="font-medium text-gray-700 dark:text-zinc-200">{toolInput?.title || 'Untitled'}</span>
            <span className="text-gray-400 dark:text-zinc-500"> · opened in Documents</span>
          </span>
        </div>
      );
    }
  }

  if (toolName === 'create_doc_folder') {
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>Creating folder…</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          Couldn’t create the folder: {toolResult.error}
        </div>
      );
    }
    if (toolResult !== undefined) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <FolderPlus size={13} className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
          <span>
            Created folder <span className="font-medium text-gray-700 dark:text-zinc-200">{toolInput?.name || 'Untitled'}</span>
            <span className="text-gray-400 dark:text-zinc-500"> · Documents</span>
          </span>
        </div>
      );
    }
  }

  // Task Manager: clean one-line statuses (no tool name / input / result).
  if (toolName === 'create_board') {
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>Creating board{toolInput?.name ? ` “${toolInput.name}”` : ''}…</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">
          Couldn’t create the board: {toolResult.error}
        </div>
      );
    }
    if (toolResult !== undefined) {
      const listCount = (toolInput?.lists || []).filter((n) => typeof n === 'string' && n.trim()).length;
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <LayoutGrid size={13} className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
          <span>
            Created board <span className="font-medium text-gray-700 dark:text-zinc-200">{toolInput?.name || 'Untitled'}</span>
            {listCount > 0 && ` with ${listCount} list${listCount === 1 ? '' : 's'}`}
            <span className="text-gray-400 dark:text-zinc-500"> · opened in Task Manager</span>
          </span>
        </div>
      );
    }
  }

  if (toolName === 'create_list' || toolName === 'create_task' || toolName === 'update_task' || toolName === 'write_task_description') {
    if (toolResult === null) {
      const verb = toolName === 'create_list' ? 'Adding list'
        : toolName === 'create_task' ? 'Adding task'
          : toolName === 'write_task_description' ? 'Writing task description'
            : 'Updating task';
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>{verb}…</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400">{toolResult.error}</div>
      );
    }
    if (toolResult !== undefined) {
      const Icon = toolName === 'create_list' ? ListPlus
        : toolName === 'update_task' ? Check : Plus;
      const label = toolName === 'create_list'
        ? <>Added list <span className="font-medium text-gray-700 dark:text-zinc-200">{toolInput?.name || 'Untitled'}</span></>
        : toolName === 'create_task'
          ? <>Added task <span className="font-medium text-gray-700 dark:text-zinc-200">{toolInput?.title || 'Untitled'}</span></>
          : toolName === 'write_task_description'
            ? <>Wrote task description</>
            : toolInput?.done ? <>Marked task done</> : <>Updated task</>;
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Icon size={13} className="flex-shrink-0 text-blue-600 dark:text-blue-300" />
          <span>{label}</span>
        </div>
      );
    }
  }

  // Open / rename / delete (Documents + Task Manager): clean one-line statuses.
  if (['open_document', 'open_board', 'rename_document', 'rename_board', 'delete_document', 'delete_board'].includes(toolName)) {
    const running = {
      open_document: 'Opening document…',
      open_board: 'Opening board…',
      rename_document: 'Renaming document…',
      rename_board: 'Renaming board…',
      delete_document: 'Deleting document…',
      delete_board: 'Deleting board…',
    }[toolName];
    if (toolResult === null) {
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Loader size={12} className="animate-spin flex-shrink-0" />
          <span>{running}</span>
        </div>
      );
    }
    if (toolResult?.error) {
      return <div className="mt-2 text-xs text-red-600 dark:text-red-400">{toolResult.error}</div>;
    }
    if (toolResult?.cancelled) {
      return <div className="mt-2 text-xs text-gray-400 dark:text-zinc-500">Cancelled</div>;
    }
    if (toolResult !== undefined) {
      const isDelete = toolName.startsWith('delete');
      const isRename = toolName.startsWith('rename');
      const Icon = isDelete ? Trash2 : isRename ? Pencil : ExternalLink;
      const surface = toolName.endsWith('board') ? 'Task Manager' : 'Documents';
      const iconColor = isDelete ? 'text-red-500 dark:text-red-400' : 'text-blue-600 dark:text-blue-300';
      const displayName = toolResult?.name || toolResult?.title
        || toolInput?.title || toolInput?.name || toolInput?.board_name || 'item';
      const verb = isDelete ? 'Deleted' : isRename ? 'Renamed to' : 'Opened';
      return (
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
          <Icon size={13} className={`flex-shrink-0 ${iconColor}`} />
          <span>
            {verb} <span className="font-medium text-gray-700 dark:text-zinc-200">{displayName}</span>
            <span className="text-gray-400 dark:text-zinc-500"> · {surface}</span>
          </span>
        </div>
      );
    }
  }

  // Rich result views (components/aiChat/toolResultViews.js). Sparse by
  // design: a tool with no renderer, or a renderer that can't make sense of
  // the result it got, returns null and falls through to the JSON block
  // below — so this stays incremental and can never render less than before.
  const richResult = renderToolResult(toolName, toolResult, toolInput, { interactive });
  if (richResult) return richResult;

  return (
    <div className="ai-chat-tool-inline mt-2 text-xs">
      <button
        type="button"
        className="ai-chat-tool-header"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown size={12} className="flex-shrink-0 opacity-70" /> : <ChevronRight size={12} className="flex-shrink-0 opacity-70" />}
        <span className="font-mono font-semibold text-blue-700 dark:text-blue-300">{toolName}</span>
        <span className="ml-1 truncate text-gray-500 dark:text-zinc-400">{summary}</span>
      </button>
      {open && (
        <div className="ai-chat-tool-body">
          <ToolCodeBlock label="Input" value={toolInput} />
          {toolResult !== undefined && (
            toolResult === null
              ? (
                <div className="mt-2 flex items-center gap-2 text-gray-500 dark:text-zinc-400" style={{ fontSize: 11 }}>
                  <Loader size={12} className="animate-spin flex-shrink-0" />
                  <span>Running…</span>
                </div>
              )
              : <ToolCodeBlock label="Result" value={toolResult} />
          )}
        </div>
      )}
    </div>
  );
});
